import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./InfoTip.scss";

// Small "i" icon that shows `text` on hover/focus. Rendered in a portal so it
// isn't clipped by scrolling tables or cards. Pass `children` to use any element
// (e.g. an icon) as the trigger instead of the "i" — a custom tooltip like this
// also works where the native `title` tooltip doesn't (Safari over SVG icons).
// `width="auto"` sizes the bubble to its text.
const InfoTip = ({ text, width = 260, style, children, className }) => {
  const iconRef = useRef(null);
  const [pos, setPos] = useState(null);
  const autoWidth = width === "auto";

  const show = () => {
    const rect = iconRef.current?.getBoundingClientRect();
    if (!rect) return;
    const openUp = window.innerHeight - rect.bottom < 140 && rect.top > 140;
    const vertical = openUp ? { bottom: window.innerHeight - rect.top + 6 } : { top: rect.bottom + 6 };
    if (autoWidth) {
      // Centre on the trigger; the bubble's max-width keeps it on screen near the edges.
      const centre = Math.max(8, Math.min(rect.left + rect.width / 2, window.innerWidth - 8));
      setPos({ left: centre, transform: "translateX(-50%)", ...vertical });
      return;
    }
    const left = Math.max(8, Math.min(rect.left + rect.width / 2 - width / 2, window.innerWidth - width - 8));
    setPos({ left, ...vertical });
  };
  const hide = () => setPos(null);

  return (
    <>
      <span
        ref={iconRef}
        className={children ? `info-tip-trigger${className ? ` ${className}` : ""}` : "info-tip-icon"}
        style={style}
        tabIndex={0}
        role="img"
        aria-label={typeof text === "string" ? text : "More info"}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onClick={(e) => e.stopPropagation()}
      >
        {children || "i"}
      </span>
      {pos && createPortal(
        <div
          className={`info-tip-bubble${autoWidth ? " info-tip-bubble--auto" : ""}`}
          style={autoWidth ? pos : { ...pos, width }}
        >
          {text}
        </div>,
        document.body
      )}
    </>
  );
};

export default InfoTip;
